import React, { useState } from 'react';
import { Box, Button, Checkbox, Chip, Collapse, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

// ── Design tokens ─────────────────────────────────────────────────────────────
const PURPLE = {
  main: '#7C3AED', light: '#C4B5FD', bg: '#F5F3FF',
  bgHover: '#EDE9FE', dark: '#4C1D95', info: '#5B21B6', muted: '#6D28D9',
};
const INDIGO = { main: '#6366F1' };
const BADGE_BASE = { display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '10px', flexShrink: 0 };
const CAPTION_SM = { fontSize: '11px' };
const PRIORITY_COLOR = { Highest: '#DC2626', High: '#F97316', Medium: '#EAB308', Low: '#22C55E', Lowest: '#86EFAC' };

export const CrossMatchBadge = ({ matchKey, strength }) => {
  if (!matchKey) return null;
  const isStrong = strength === 'strong';
  const color = isStrong ? '#92400E' : '#1D4ED8';
  return (
    <Tooltip placement="top" title={
      isStrong
        ? `Very similar to existing TC ${matchKey}. Consider reusing it from the "Existing Matches" section below.`
        : `Possibly similar to existing TC ${matchKey}. Review before creating.`
    }>
      <Box sx={{ ...BADGE_BASE, gap: 0.5, bgcolor: isStrong ? '#FEF3C7' : '#EFF6FF', border: `1px solid ${isStrong ? '#FCD34D' : '#BFDBFE'}`, cursor: 'default' }}>
        {isStrong
          ? <WarningAmberIcon sx={{ fontSize: 11, color }} />
          : <InfoOutlinedIcon sx={{ fontSize: 11, color }} />}
        <Typography variant="caption" sx={{ ...CAPTION_SM, fontWeight: 600, color }}>
          {isStrong ? `Similar to ${matchKey}` : `Possible overlap · ${matchKey}`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── AiMatchBadge — shown on existing TC cards ─────────────────────────────────
const AiMatchBadge = ({ strength }) => {
  if (!strength) return null;
  const isStrong = strength === 'strong';
  return (
    <Box sx={{ ...BADGE_BASE, bgcolor: isStrong ? '#FEF3C7' : '#EFF6FF', border: `1px solid ${isStrong ? '#FCD34D' : '#BFDBFE'}` }}>
      <Typography variant="caption" sx={{ ...CAPTION_SM, fontWeight: 700, color: isStrong ? '#92400E' : '#1D4ED8' }}>
        {isStrong ? 'Strong match' : 'Possible match'}
      </Typography>
    </Box>
  );
};

// ── Individual existing TC card ───────────────────────────────────────────────
const ExistingMatchCard = ({ match, isSelected, onToggle, disabled, projectId, linkable }) => {
  const priorityColor = PRIORITY_COLOR[match.priority] ?? '#9CA3AF';
  // Backend sends _aiMatchStrength (with underscore prefix)
  const aiMatchStrength = match.ai_match_strength ?? null;

  return (
    <Box sx={{
      border: '1.5px solid',
      borderColor: linkable && isSelected ? INDIGO.main : '#D1D5DB',
      borderRadius: 1.5,
      overflow: 'hidden',
      bgcolor: isSelected ? PURPLE.bg : '#fff',
      transition: 'border-color 0.15s, background-color 0.15s',
    }}>
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, alignItems: 'flex-start' }}>
        {linkable && (
          <Tooltip placement="top" title={isSelected ? 'Deselect — will not be linked' : 'Select — will be linked to the Jira issue'}>
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => onToggle(match.id)}
              disabled={disabled}
              inputProps={{ 'aria-label': `Select existing TC: ${match.summary}` }}
              sx={{ mt: 0.25, flexShrink: 0, color: INDIGO.main, '&.Mui-checked': { color: INDIGO.main } }}
            />
          </Tooltip>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Tooltip placement="top" title="Open test case in new tab">
              <Box
                component="a"
                href={`/project/${projectId}/testcases/${match.key}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontWeight: 700, fontSize: '12px', color: INDIGO.main, textDecoration: 'none', flexShrink: 0, '&:hover': { textDecoration: 'underline' } }}
              >
                {match.key}<OpenInNewIcon sx={{ fontSize: 11 }} />
              </Box>
            </Tooltip>
            <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0, fontSize: '13px' }}>
              {match.summary}
            </Typography>
            <AiMatchBadge strength={aiMatchStrength} />
          </Box>

          {match.objective && (
            <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5, fontSize: '12px', mb: 0.75 }}>
              {match.objective}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
            <Chip
              label={match.status}
              size="small"
              sx={{ height: 20, ...CAPTION_SM, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', bgcolor: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB', borderRadius: '6px', '& .MuiChip-label': { px: 0.75 } }}
            />
            {match.priority && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <FlagOutlinedIcon sx={{ fontSize: 12, color: priorityColor }} />
                <Typography variant="caption" sx={{ ...CAPTION_SM, fontWeight: 500, color: priorityColor }}>
                  {match.priority}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const MatchingTestCases = ({ existingMatches = [], selectedIds, onToggle, disabled = false, projectId, linkable = true }) => {
  const [expanded, setExpanded] = useState(true);

  if (!existingMatches.length) return null;

  const selectedCount = existingMatches.filter((m) => selectedIds.has(m.id)).length;

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        fullWidth
        onClick={() => setExpanded((v) => !v)}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          display: 'flex', justifyContent: 'space-between', px: 1.5, py: 1,
          bgcolor: PURPLE.bg, border: `1.5px solid ${PURPLE.light}`,
          borderRadius: expanded ? '8px 8px 0 0' : '8px',
          textTransform: 'none', color: PURPLE.dark, fontWeight: 600, fontSize: '13px',
          '&:hover': { bgcolor: PURPLE.bgHover },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCopyIcon sx={{ fontSize: 15, color: PURPLE.main }} />
          <span>Existing Matches</span>
          <Chip label={existingMatches.length} size="small" sx={{ height: 18, ...CAPTION_SM, fontWeight: 700, bgcolor: PURPLE.main, color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
          {linkable && selectedCount > 0 && (
            <Typography variant="caption" sx={{ ...CAPTION_SM, color: PURPLE.muted }}>
              {selectedCount} selected to link
            </Typography>
          )}
        </Box>
      </Button>

      <Collapse in={expanded}>
        <Box sx={{ border: `1.5px solid ${PURPLE.light}`, borderTop: 'none', borderRadius: '0 0 8px 8px', p: 1.5, bgcolor: '#FDFCFF', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, p: 1, bgcolor: PURPLE.bgHover, borderRadius: 1, mb: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: PURPLE.main, mt: 0.1, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: PURPLE.info, lineHeight: 1.5 }}>
              {linkable
                ? 'These test cases already exist and cover similar scenarios. Select them to link to this issue instead of creating duplicates.'
                : 'These test cases already exist and cover similar scenarios. Review them to avoid creating duplicates.'}
            </Typography>
          </Box>
          {existingMatches.map((match) => (
            <ExistingMatchCard
              key={match.id}
              match={match}
              isSelected={selectedIds.has(match.id)}
              onToggle={onToggle}
              disabled={disabled}
              projectId={projectId}
              linkable={linkable}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default MatchingTestCases;